/**
 * Fetch wrapper for the todo.placemetry.com REST API (`/api/v1`).
 *
 * The browser authenticates with the HttpOnly session cookie set by
 * `POST /api/v1/auth/login`, so every request sends `credentials: 'include'`.
 * Errors are normalised into `ApiError`, which carries the HTTP status and the
 * FastAPI `detail` payload so callers can branch on 401 vs 422.
 */

export const API_BASE = '/api/v1'

export class ApiError extends Error {
  /**
   * @param {number} status HTTP status code (0 when the network call failed).
   * @param {string} message Human-readable message pulled from `detail`.
   * @param {unknown} detail Raw `detail` value from the API response body.
   */
  constructor(status, message, detail) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

/** Turn FastAPI's `detail` (string, or list of validation errors) into text. */
function detailToMessage(detail, fallback) {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const loc = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : ''
        return loc ? `${loc}: ${d.msg}` : d.msg
      })
      .join('; ')
  }
  return fallback
}

/**
 * Issue a request against the API.
 *
 * @param {string} path Path below `/api/v1`, e.g. `/tickets`.
 * @param {{method?: string, body?: unknown, params?: Record<string, unknown>, signal?: AbortSignal}} [opts]
 * @returns {Promise<any>} Parsed JSON, or `null` for `204 No Content`.
 * @throws {ApiError} On any non-2xx response or network failure.
 */
export async function request(path, opts = {}) {
  const { method = 'GET', body, params, signal } = opts
  const url = new URL(API_BASE + path, window.location.origin)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue
      if (Array.isArray(value)) {
        for (const v of value) if (v !== '' && v != null) url.searchParams.append(key, String(v))
      } else {
        url.searchParams.set(key, String(value))
      }
    }
  }

  let response
  try {
    response = await fetch(url.pathname + url.search, {
      method,
      credentials: 'include',
      signal,
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (cause) {
    if (cause && cause.name === 'AbortError') throw cause
    throw new ApiError(0, 'network error - is the API running?', null)
  }

  if (response.status === 204) return null

  const text = await response.text()
  let payload = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    const detail = payload && typeof payload === 'object' ? payload.detail : text
    throw new ApiError(
      response.status,
      detailToMessage(detail, `${response.status} ${response.statusText}`),
      detail,
    )
  }
  return payload
}

/** Endpoint surface, mirroring `todo_api/routers/`. */
export const api = {
  // auth
  me: () => request('/auth/me'),
  login: (email, password) => request('/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => request('/auth/logout', { method: 'POST' }),

  // taxonomy
  users: () => request('/users'),
  cities: () => request('/cities'),
  components: () => request('/components'),
  assets: () => request('/assets'),

  /**
   * List tickets. `filters` keys match the FastAPI query params exactly:
   * status, city, component, assignee, kind, priority, asset (repeatable),
   * plus q, open_only, sort, limit, offset.
   */
  tickets: (filters, signal) => request('/tickets', { params: filters, signal }),
  ticket: (keyOrId, signal) => request(`/tickets/${encodeURIComponent(keyOrId)}`, { signal }),
  createTicket: (payload) => request('/tickets', { method: 'POST', body: payload }),
  updateTicket: (keyOrId, patch) =>
    request(`/tickets/${encodeURIComponent(keyOrId)}`, { method: 'PATCH', body: patch }),

  comments: (keyOrId) => request(`/tickets/${encodeURIComponent(keyOrId)}/comments`),
  addComment: (keyOrId, body) =>
    request(`/tickets/${encodeURIComponent(keyOrId)}/comments`, { method: 'POST', body: { body } }),
  deleteComment: (id) => request(`/comments/${id}`, { method: 'DELETE' }),

  citySummary: (slug) => request(`/cities/${encodeURIComponent(slug)}/summary`),
  assetTickets: (id) => request(`/assets/${id}/tickets`),
  graph: (params) => request('/graph', { params }),

  updateComment: (id, body) => request(`/comments/${id}`, { method: 'PATCH', body: { body } }),

  claimTicket: (keyOrId, note) =>
    request(`/tickets/${encodeURIComponent(keyOrId)}/claim`, {
      method: 'POST',
      body: { note: note || null, force: false },
    }),
  releaseTicket: (keyOrId) =>
    request(`/tickets/${encodeURIComponent(keyOrId)}/release`, { method: 'POST' }),

  /** Controlled vocabularies and the legal status transitions (server policy). */
  meta: () => request('/meta'),

  createLink: (srcKey, dstKey, type) =>
    request('/links', { method: 'POST', body: { src_key: srcKey, dst_key: dstKey, type } }),
  deleteLink: (id) => request(`/links/${id}`, { method: 'DELETE' }),

  /** Ask Claude about one ticket, against its own recorded context. */
  askTicket: (keyOrId, question, effort = 'low', save = true) =>
    request(`/tickets/${encodeURIComponent(keyOrId)}/ask`, {
      method: 'POST',
      body: { question, effort, save },
    }),
}
